import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { TypedOrder } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { Card } from '../../../shared/components/ui/Card';

interface OrdersResponse {
  data: TypedOrder[];
  pagination: { page: number; per_page: number; total: number };
}

/**
 * Pending Submission — a review queue for e-filing orders that have been
 * assembled (in the portal, by a concierge operator, or via the MCP
 * integration) and marked READY FOR REVIEW, awaiting the human's final review,
 * fee calculation, and submission. Distinct from /drafts (every work-in-progress
 * draft): this surface lists e-filing orders by their durable stored status.
 *
 * An order appears here exactly when its stored status is `ready-for-review`
 * (R-STORED-SURFACE) — set server-side by markReadyForReview once the order is
 * assembled and locally valid. Readiness is NOT recomputed client-side from
 * typeData; the stored status is the single source of truth (INV-STORED-SURFACE).
 * This supersedes the #734 client-side `assessReadiness` recompute MVP.
 */

const CustomerPendingSubmission: React.FC = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const { get, del } = useApi();

  const [orders, setOrders] = useState<TypedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /* eslint-disable @eslint-react/set-state-in-effect -- loading flag before async fetch */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    setLoading(true);
    get<OrdersResponse>(`${API_BASE_URL}/api/orders?status=ready-for-review&type=e-filing&perPage=50`)
      // Filter on the stored status client-side too, so the listing is exactly
      // the ready-for-review orders regardless of what the response carries
      // (R-STORED-SURFACE: listedInPendingSubmission(o) <-> status(o) = ready-for-review).
      .then((res) => setOrders(res.data.filter((o) => o.status === 'ready-for-review')))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [get]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  // Edit resumes the order in the wizard (server rehydration via ?orderId=),
  // dropping the reviewer at the steps that stay in the portal: Court Fees → Submit.
  const editInWizard = (order: TypedOrder) => navigate(`/eFiling?orderId=${order.id}`);

  // Delete is portal-only (no MCP delete tool — consequence boundary) and the
  // backend enforces draft-only, so this never removes a submitted filing.
  const deleteOrder = async (order: TypedOrder) => {
    if (!window.confirm(`Delete ${order.referenceNumber}? This permanently removes the draft and cannot be undone.`)) {
      return;
    }
    setDeletingId(order.id);
    setError(null);
    try {
      await del(`${API_BASE_URL}/api/orders/${order.id}`);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete order');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <PageTitle>Pending Submission</PageTitle>

      <p className="text-sm mb-4" style={{ color: brand.colors.textMuted }}>
        eFiling orders prepared and awaiting your review, fee calculation, and submission —
        including orders staged by a concierge operator or assembled through the Claude (MCP)
        integration. Final fee calculation and court submission always happen here in the portal.
      </p>

      {loading && (
        <p className="text-sm py-8 text-center" style={{ color: brand.colors.textLight }}>
          Loading pending orders...
        </p>
      )}

      {error && (
        <Card className="mb-4">
          <p className="text-sm text-red-500">Failed to load pending orders: {error}</p>
        </Card>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="text-center py-12">
          <h3 className="text-xl font-light mb-2" style={{ color: brand.colors.textLight }}>
            Nothing pending submission.
          </h3>
          <p className="text-sm" style={{ color: brand.colors.textMuted }}>
            eFiling orders prepared for you to review and submit will appear here.
          </p>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              {orders.length} pending
            </p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: brand.colors.border }}>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Order #</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Case</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Status</th>
                <th className="text-right text-xs font-medium py-2" style={{ color: brand.colors.textLight }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr
                  key={order.id}
                  className="border-b cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: brand.colors.border }}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className="py-3 pr-4">
                    <span className="text-sm font-medium" style={{ color: brand.colors.link }}>
                      {order.referenceNumber}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.text }}>
                      {order.caseTitle || order.caseNumber || '—'}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Ready for review
                    </span>
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editInWizard(order);
                      }}
                      className="px-3 py-1 rounded text-xs font-medium mr-2"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOrder(order);
                      }}
                      disabled={deletingId === order.id}
                      className="px-3 py-1 rounded text-xs font-medium border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {deletingId === order.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default CustomerPendingSubmission;
